import React from 'react';
import {useAppDraftGuard} from '../app/navigation/index.js';
import type {OwnerEquipmentReport} from '../app/data/ownerEquipmentReportsData.js';
import type {OwnerReportActor} from '../lib/ownerReportAccess.js';
import {canDeleteOwnerFueling, canEditOwnerFueling, canEditOwnerFuelSupply} from '../lib/ownerReportAccess.js';
import {deleteOwnFueling, deleteOwnFuelSupply} from '../lib/ownerReportsApi.js';
import type {SupabaseClient} from '../lib/supabaseTypes.js';
import {forgetSave} from '../lib/saveStatus.js';
import {fmtCentralDateTime} from '../lib/dateUtils.js';
import {controlStyle} from '../lib/formStyles.js';
import {createRecordAutosave, type RecordAutosaveState} from '../tasks/recordAutosave.js';
import {
  createOwnerReportWriter,
  ownerReportDraft,
  ownerReportFields,
  validateOwnerReportDraft,
} from './ownerReportEditor.js';
import Drawer from '../shared/Drawer.js';
import Modal, {ModalFooter} from '../shared/Modal.js';
import Button from '../shared/Button.js';
import Field from '../shared/Field.js';
import DateInput from '../shared/DateInput.js';
import NumberInput from '../shared/NumberInput.js';
import LockedValue from '../shared/LockedValue.js';
import InlineNotice from '../shared/InlineNotice.js';
import {PrivateStorageOpenButton} from '../shared/PrivateStorageObject.js';

type Props = {
  client: SupabaseClient;
  actor: OwnerReportActor;
  report: OwnerEquipmentReport;
  onClose: () => void;
  onDeleted: () => void;
};
export default function OwnerReportDrawer({client, actor, report, onClose, onDeleted}: Props) {
  // The parent keys this editor by report and role. Keep its draft owner stable
  // throughout that session: a list refresh must never replace unsaved input.
  const [fields] = React.useState(() => ownerReportFields(report, actor.role));
  const [initial] = React.useState(() => ownerReportDraft(report, fields));
  const [draft, setDraft] = React.useState(initial);
  const [state, setState] = React.useState<RecordAutosaveState>('idle');
  const [error, setError] = React.useState('');
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const deletingRef = React.useRef(false);
  const mounted = React.useRef(true);
  const key = `owner-report:${report.kind}:${report.row.id}`;
  const canEdit =
    report.kind === 'fueling'
      ? canEditOwnerFueling(actor, report.row.owner_profile_id)
      : canEditOwnerFuelSupply(actor, report.row.owner_profile_id);
  const canDelete = report.kind === 'fueling' ? canDeleteOwnerFueling(actor, report.row.owner_profile_id) : canEdit;
  const [autosave] = React.useState(() => {
    const write = createOwnerReportWriter(client, report, fields);
    return createRecordAutosave({
      key,
      initial,
      onDraft: setDraft,
      onState: setState,
      write: async (value) => {
        try {
          await write(value);
          if (mounted.current) setError('');
        } catch (cause) {
          if (mounted.current) setError(cause instanceof Error ? cause.message : String(cause));
          throw cause;
        }
      },
    });
  });
  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      autosave.dispose();
      forgetSave(key);
    };
  }, [autosave, key]);
  useAppDraftGuard({
    ownerKey: key,
    isUnsafeToLeave: () => deletingRef.current || autosave.isDirty(),
    settleBeforeLeave: async () => !deletingRef.current && (await autosave.flush()),
  });
  const errors = validateOwnerReportDraft(draft, fields);
  const title = report.kind === 'fueling' ? report.equipmentName + ' Fueling' : 'Fuel Supply';
  const change = (field: string, value: string) => {
    if (canEdit && !deletingRef.current) autosave.update({[field]: value});
  };
  async function close() {
    if (!deletingRef.current && (await autosave.flush())) onClose();
  }
  async function requestDelete() {
    if (canDelete && !deletingRef.current && (await autosave.flush())) setConfirmDelete(true);
  }
  async function remove() {
    if (!canDelete || deletingRef.current) return;
    deletingRef.current = true;
    setDeleting(true);
    setError('');
    try {
      if (report.kind === 'fueling') await deleteOwnFueling(client, report.row);
      else await deleteOwnFuelSupply(client, report.row.id);
      deletingRef.current = false;
      setDeleting(false);
      setConfirmDelete(false);
      onDeleted();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
      deletingRef.current = false;
      setDeleting(false);
    }
  }
  const photos = report.kind === 'fueling' && Array.isArray(report.row.photos) ? report.row.photos : [];
  return (
    <>
      <Drawer
        title={title}
        autosave
        onClose={() => void close()}
        drawerAttrs={{'data-owner-report-drawer': report.row.id}}
        footer={
          <ModalFooter
            onDelete={canDelete && !deleting ? () => void requestDelete() : undefined}
            busy={deleting}
            busyLabel="Deleting…"
            primaryDisabled={deleting}
          />
        }
      >
        <div style={{display: 'grid', gap: 16}}>
          {error && (
            <InlineNotice
              notice={{kind: 'error', message: error}}
              action={
                !deleting && !confirmDelete ? <Button onClick={() => void autosave.retry()}>Retry</Button> : undefined
              }
            />
          )}
          {state === 'failed' && !error && (
            <InlineNotice
              notice={{kind: 'error', message: 'Not Saved — Retry'}}
              action={<Button onClick={() => void autosave.retry()}>Retry</Button>}
            />
          )}
          <LockedValue
            label="Submitted By"
            value={report.row.team_member || 'Unknown'}
            source="Name recorded on this report; its owner stays unchanged"
          />
          <LockedValue
            label="Submitted"
            value={fmtCentralDateTime(String(report.row.submitted_at || report.row.created_at || '')) || 'Unknown'}
            source="Original submission time"
          />
          {!canEdit && <p style={{color: 'var(--text-2)'}}>View only — you cannot edit this report.</p>}
          {fields.map((field) =>
            !canEdit ? (
              <LockedValue key={field.key} label={field.label} value={draft[field.key] || '—'} />
            ) : (
              <Field key={field.key} label={field.label} required={field.required} error={errors[field.key]}>
                {field.type === 'date' ? (
                  <DateInput
                    value={draft[field.key]}
                    onChange={(value) => change(field.key, value)}
                    onBlur={() => void autosave.flush()}
                    disabled={deleting}
                  />
                ) : field.type === 'number' ? (
                  <NumberInput
                    value={draft[field.key]}
                    onChange={(value) => change(field.key, value)}
                    onBlur={() => void autosave.flush()}
                    disabled={deleting}
                  />
                ) : field.type === 'notes' ? (
                  <textarea
                    style={{...controlStyle, minHeight: 96, resize: 'vertical'}}
                    value={draft[field.key]}
                    onChange={(event) => change(field.key, event.target.value)}
                    onBlur={() => void autosave.flush()}
                    disabled={deleting}
                  />
                ) : (
                  <input
                    style={controlStyle}
                    value={draft[field.key]}
                    onChange={(event) => change(field.key, event.target.value)}
                    onBlur={() => void autosave.flush()}
                    disabled={deleting}
                  />
                )}
              </Field>
            ),
          )}
          {photos.length > 0 && (
            <section aria-label="Report Photos">
              <h3>Photos</h3>
              <div style={{display: 'flex', gap: 8, flexWrap: 'wrap'}}>
                {photos.map((photo, index) => {
                  const object = photo && typeof photo === 'object' ? (photo as Record<string, unknown>) : {};
                  return (
                    <PrivateStorageOpenButton
                      key={index}
                      bucket="equipment-maintenance-docs"
                      path={object.path}
                      legacyUrl={object.url}
                    >
                      Open {typeof object.name === 'string' ? object.name : `Photo ${index + 1}`}
                    </PrivateStorageOpenButton>
                  );
                })}
              </div>
              <p style={{color: 'var(--text-2)', fontSize: 13}}>Photos stay with the original report.</p>
            </section>
          )}
        </div>
      </Drawer>
      {confirmDelete && (
        <Modal
          size="sm"
          title={`Delete ${title} on ${report.row.date || 'this date'}?`}
          onClose={() => {
            if (!deletingRef.current) setConfirmDelete(false);
          }}
          footer={
            <ModalFooter
              primaryLabel="Delete"
              primaryVariant="destructive"
              onPrimary={() => void remove()}
              busy={deleting}
              busyLabel="Deleting…"
              cancelDisabled={deleting}
            />
          }
        >
          <p>The report and its photos will be hidden. An administrator can restore them.</p>
          {error && <InlineNotice notice={{kind: 'error', message: error}} />}
        </Modal>
      )}
    </>
  );
}
